// Export utilities — Excel (SheetJS) and PDF (jsPDF + AutoTable).
import React from 'react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // side-effect import: extends jsPDF prototype with .autoTable()
import { Icon, Button } from './ui.jsx';

function fmtUSD(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function buildRepReport(rep, invoices, period, tenant) {
  // Defensive: rep can be null while the admin's "View as" preview is
  // still loading. Fall back to whatever invoices were handed in
  // (already RLS-scoped or already filtered) so we never crash and
  // produce an empty-but-valid report.
  const repId = rep?.id || rep?.repUserId || null;
  const mine = repId ? invoices.filter(i => i.repId === repId) : invoices;
  const summary = {
    rep: rep?.name || '—',
    email: rep?.email || '',
    tenant: tenant ? tenant.name : '',
    period,
    invoiceCount: mine.length,
    totalBill:   mine.reduce((a,i) => a + i.totalBill, 0),
    grossProfit: mine.reduce((a,i) => a + i.gp, 0),
    aca:         mine.reduce((a,i) => a + i.aca, 0),
    sick:        mine.reduce((a,i) => a + i.sick, 0),
    commission:  mine.filter(i => !i.excluded).reduce((a,i) => a + i.commission, 0),
    unpaid:      mine.filter(i => !i.paid).reduce((a,i) => a + i.outstanding, 0),
    carriedForward: mine.filter(i => !i.paid && i.ageDays > 30).length,
    flagged90:   mine.filter(i => !i.paid && i.ageDays > 90).length,
  };
  const rows = mine.map(i => ({
    Client:     i.client,
    Invoice:    i.doc,
    Date:       i.date,
    Branch:     i.branch,
    TotalBill:  i.totalBill,
    GP:         i.gp,
    ACA:        i.aca,
    SICK:       i.sick,
    NET:        i.net,
    Tier:       i.tier === 'y1' ? 'Y1 (new)' : 'Y2+',
    Rate:       (Number(i.rate || 0) * 100).toFixed(2) + '%',
    Commission: i.commission,
    Status:     i.paid ? 'Paid' : 'Unpaid',
    AgeDays:    i.paid ? '' : i.ageDays,
    Flag:       !i.paid && i.ageDays > 90 ? 'OVERDUE >90d'
               : !i.paid && i.ageDays > 30 ? 'Carried forward'
               : '',
    Outstanding: i.paid ? 0 : i.outstanding,
  }));
  return { summary, rows, rep, period };
}

// =================== EXCEL ===================
// Excel cell number formats. Negatives in red, currency with two decimals,
// matches what Finance teams expect to see in payroll-bound exports.
const FMT_USD = '"$"#,##0.00_);[Red]("$"#,##0.00)';
const FMT_INT = '#,##0';

// Apply a Z-format to a specific cell, only if it's a real number cell.
// SheetJS otherwise leaves it as raw and Excel shows '4438.27' instead of
// '$4,438.27'. Guards against undefined cells (skipped rows).
function setCellFormat(ws, addr, z) {
  const c = ws[addr];
  if (c && c.t === 'n') c.z = z;
}

export function exportRepExcel(rep, invoices, period, tenant) {
  const { summary, rows } = buildRepReport(rep, invoices, period, tenant);
  const wb = XLSX.utils.book_new();

  // Summary sheet
  const summaryAoA = [
    ['Sales Commission — Rep Report'],
    [],
    ['Rep',               summary.rep],
    ['Email',             summary.email],
    ['Tenant',            summary.tenant],
    ['Period',            summary.period],
    ['Generated',         new Date().toISOString().slice(0,19).replace('T',' ')],
    [],
    ['Invoices',          summary.invoiceCount],
    ['Total Bill',        summary.totalBill],
    ['Gross Profit',      summary.grossProfit],
    ['ACA deduction',    -summary.aca],
    ['SICK deduction',   -summary.sick],
    ['NET (GP − ACA − SICK)', summary.grossProfit - summary.aca - summary.sick],
    ['Commission',        summary.commission],
    [],
    ['Unpaid outstanding',        summary.unpaid],
    ['Carried forward (>30d)',    summary.carriedForward],
    ['Flagged overdue (>90d)',    summary.flagged90],
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(summaryAoA);
  ws1['!cols'] = [{ wch: 28 }, { wch: 32 }];
  // Column B = currency rows + plain-int rows. Indices match summaryAoA
  // 0-based row numbers (Excel rows = JS index + 1).
  setCellFormat(ws1, 'B9',  FMT_INT); // Invoices count
  setCellFormat(ws1, 'B10', FMT_USD); // Total Bill
  setCellFormat(ws1, 'B11', FMT_USD); // Gross Profit
  setCellFormat(ws1, 'B12', FMT_USD); // ACA
  setCellFormat(ws1, 'B13', FMT_USD); // SICK
  setCellFormat(ws1, 'B14', FMT_USD); // NET
  setCellFormat(ws1, 'B15', FMT_USD); // Commission
  setCellFormat(ws1, 'B17', FMT_USD); // Unpaid outstanding
  setCellFormat(ws1, 'B18', FMT_INT); // Carried forward count
  setCellFormat(ws1, 'B19', FMT_INT); // Flagged overdue count
  XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

  // Invoices sheet — apply USD formatting to every currency column.
  // Header row stays as text; data rows get the format.
  const ws2 = XLSX.utils.json_to_sheet(rows);
  ws2['!cols'] = [
    { wch: 30 }, { wch: 14 }, { wch: 12 }, { wch: 10 },
    { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
    { wch: 10 }, { wch: 8 },  { wch: 14 },
    { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 14 },
  ];
  // Invoices column letters (matches json_to_sheet column order):
  //   A Client  B Invoice  C Date  D Branch  E TotalBill  F GP
  //   G ACA  H SICK  I NET  J Tier  K Rate  L Commission
  //   M Status  N AgeDays  O Flag  P Outstanding
  const usdCols = ['E','F','G','H','I','L','P'];
  // Apply per row starting at Excel row 2 (row 1 = header).
  for (let i = 0; i < rows.length; i++) {
    const xlRow = i + 2;
    for (const col of usdCols) setCellFormat(ws2, col + xlRow, FMT_USD);
  }
  XLSX.utils.book_append_sheet(wb, ws2, 'Invoices');

  const safeName = (rep?.name || 'rep').replace(/\s+/g,'_');
  const safePeriod = String(period || 'period').replace(/\s+/g,'_');
  const fname = `commission_${safeName}_${safePeriod}.xlsx`;
  XLSX.writeFile(wb, fname);
}

// =================== PDF ===================
export function exportRepPDF(rep, invoices, period, tenant) {
  const { summary, rows } = buildRepReport(rep, invoices, period, tenant);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();

  // Header
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text('Sales Commission — Rep Report', 40, 45);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`${rep?.name || '—'} · ${summary.tenant || ''}`, 40, 62);
  doc.text(`Period: ${period}  ·  Generated: ${new Date().toISOString().slice(0,10)}`, 40, 76);
  doc.setTextColor(0);

  // Summary boxes
  const boxes = [
    ['Invoices', String(summary.invoiceCount)],
    ['Total Bill', fmtUSD(summary.totalBill)],
    ['Gross Profit', fmtUSD(summary.grossProfit)],
    ['Commission', fmtUSD(summary.commission)],
    ['Unpaid', fmtUSD(summary.unpaid)],
    ['Overdue >90d', String(summary.flagged90)],
  ];
  const boxW = (pageW - 80) / boxes.length;
  boxes.forEach((b, i) => {
    const x = 40 + i * boxW;
    doc.setDrawColor(220); doc.setFillColor(247, 249, 252);
    doc.roundedRect(x, 100, boxW - 8, 56, 4, 4, 'FD');
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(120);
    doc.text(b[0].toUpperCase(), x + 10, 118);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(20);
    doc.text(b[1], x + 10, 140);
  });

  // Table
  const head = [['Client','Invoice','Date','Branch','Total Bill','GP','Commission','Status','Age','Flag']];
  const body = rows.map(r => [
    r.Client.length > 32 ? r.Client.slice(0, 31) + '…' : r.Client,
    r.Invoice, r.Date, r.Branch,
    fmtUSD(r.TotalBill), fmtUSD(r.GP), fmtUSD(r.Commission),
    r.Status, r.AgeDays === '' ? '—' : r.AgeDays + 'd', r.Flag || '—',
  ]);
  doc.autoTable({
    head, body, startY: 180,
    styles:    { font: 'helvetica', fontSize: 8, cellPadding: 4 },
    headStyles:{ fillColor: [48, 84, 150], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [247, 249, 252] },
    columnStyles: {
      4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
      8: { halign: 'right' },
    },
    didParseCell: (d) => {
      if (d.section === 'body' && d.column.index === 9) {
        const v = d.cell.raw;
        if (v === 'OVERDUE >90d') { d.cell.styles.textColor = [176, 20, 20]; d.cell.styles.fontStyle = 'bold'; }
        else if (v === 'Carried forward') { d.cell.styles.textColor = [150, 100, 0]; }
      }
    },
    margin: { left: 40, right: 40 },
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(140);
    doc.text(`${rep?.name || '—'} · ${period} · page ${p} of ${pageCount}`,
      pageW / 2, doc.internal.pageSize.getHeight() - 20, { align: 'center' });
  }

  const safeName = (rep?.name || 'rep').replace(/\s+/g,'_');
  const safePeriod = String(period || 'period').replace(/\s+/g,'_');
  const fname = `commission_${safeName}_${safePeriod}.pdf`;
  doc.save(fname);
}

// =================== Export button with dropdown ===================
export function ExportMenu({ rep, invoices, period, tenant, size = 'md' }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <Button size={size} variant="secondary" icon="download" onClick={() => setOpen(!open)}>Export ▾</Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)}/>
          <div className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-md shadow-lg border border-neutral-200 z-40 overflow-hidden">
            <button onClick={() => { exportRepExcel(rep, invoices, period, tenant); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-neutral-50 flex items-center gap-2.5">
              <div className="w-6 h-6 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center text-[10px] font-bold">XL</div>
              <div>
                <div className="font-medium text-neutral-900">Download as Excel</div>
                <div className="text-[11px] text-neutral-500">Summary + invoice detail</div>
              </div>
            </button>
            <button onClick={() => { exportRepPDF(rep, invoices, period, tenant); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-neutral-50 flex items-center gap-2.5 border-t border-neutral-100">
              <div className="w-6 h-6 rounded bg-red-50 border border-red-200 text-red-700 flex items-center justify-center text-[10px] font-bold">PDF</div>
              <div>
                <div className="font-medium text-neutral-900">Download as PDF</div>
                <div className="text-[11px] text-neutral-500">Print-ready report</div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

